import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Checkbox, Dropdown, Modal, Tooltip, message } from 'antd';
import classNames from 'classnames';
import { ReactComponent as CloseSvg } from '@assets/icons/cross.svg';
import { ReactComponent as SuccessSvg } from '@assets/icons/success.svg';
import { ReactComponent as LockSvg } from '@assets/icons/lock.svg';
import { ReactComponent as ApprovalRoutingSvg } from '@assets/icons/approval-routing.svg';
import { queryClient } from 'src/query-client';
import { decisions } from './utils';
import { useAssetApprovalDecision } from './hooks/useAssetApprovalDecision';
import { useDecisionApprovalWorkflow } from '@hooks/queries/approval';
import { SubmitDecisionDto } from '@api/Api';
import useOnboardingProcess from '@hooks/useOnboardingProcess';
import { useResponsive } from '@hooks/useResponsive';

import './DropdownDecisions.less';

type DropdownDecisionsAssetMobileProps = {
  password?: string | null;
  className?: string;
};

function DropdownDecisionsAssetMobile({
  password,
  className
}: DropdownDecisionsAssetMobileProps) {
  const { tourData, goToStep } = useOnboardingProcess();
  const { isMdWidth } = useResponsive();
  const isAnnouncementMediaViewerMobileTour =
    tourData?.type === 'announcement-media-viewer-mobile';
  const isAnnouncementMediaViewerMobileDecisionTour =
    isAnnouncementMediaViewerMobileTour && goToStep === 2;
  const isAnnouncementMediaViewerMobileDecisionTourStep =
    isAnnouncementMediaViewerMobileTour && goToStep === 3;
  const [isApplyToAllAssets, setIsApplyToAllAssets] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const _isDropdownOpen =
    isAnnouncementMediaViewerMobileDecisionTourStep || isDropdownOpen;

  useEffect(() => {
    const bodyClass = 'dropdown_decisions-mobile-backdrop-open';
    if (_isDropdownOpen) {
      document.body.classList.add(bodyClass);
    } else {
      document.body.classList.remove(bodyClass);
    }
    return () => document.body.classList.remove(bodyClass);
  }, [_isDropdownOpen]);

  const { mutateAsync: decisionApprovalWorkflow } = useDecisionApprovalWorkflow(
    {}
  );

  const {
    aw,
    assets,
    selectedVersion,
    decision,
    disableReason,
    filteredApprovalListAssets,
    isButtonDisabled,
    isDemoOrganization,
    isShareReviewLink,
    showDecision,
    selectDecision
  } = useAssetApprovalDecision(password);

  const hasBatchAssetsList = filteredApprovalListAssets.length > 1;

  const refetchAsset = async (assetId: string) => {
    queryClient.invalidateQueries({
      queryKey: ['assets', 'item', assetId]
    });
  };

  const selectDecisionAllAssets = async (
    decisionKey: SubmitDecisionDto['decision'],
    confirmed = false
  ) => {
    if (!assets || !selectedVersion || !aw?.id || !aw.currentStageId) return;
    if (!confirmed) {
      Modal.confirm({
        onOk: () => selectDecisionAllAssets(decisionKey, true),
        width: 500,
        icon: null,
        centered: true,
        okText: 'Yes, submit',
        cancelText: 'No, continue review',
        closable: true,
        maskClosable: true,
        closeIcon: <CloseSvg />,
        content: (
          <div className="modal_container">
            <h2
              className="modal_container__title"
              style={{ fontSize: 22, marginBottom: '0.5em' }}
            >
              Is this your final decision?
            </h2>
            <p className="modal_container__subtitle">
              Once submitted, you will not be able to leave additional comments
              or change your decision.
            </p>
          </div>
        )
      });
      return;
    }
    try {
      const versionIds = filteredApprovalListAssets.map(
        (item) => item.versionId
      );
      await decisionApprovalWorkflow({
        id: aw.id,
        versionIds,
        stageId: aw.currentStageId!,
        password,
        decision: decisionKey
      });
      await refetchAsset(assets.id);
      const batchDecisionLabel = decisions.find(
        (x) => x.key === decisionKey
      )?.title;
      setIsApplyToAllAssets(false);
      message.success({
        content: `Selected media has been marked as ${batchDecisionLabel}`,
        className: 'message-dark-modal',
        icon: <SuccessSvg className="anticon" />,
        duration: 2
      });
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Something went wrong');
    }
  };

  const showForTour = isAnnouncementMediaViewerMobileTour && !isShareReviewLink;

  if (!showDecision && !isDemoOrganization && !showForTour) {
    return null;
  }

  const onDecisionMenuClick = ({ key }: { key: string }) => {
    if (isDemoOrganization) return;
    setIsDropdownOpen(false);
    if (isApplyToAllAssets && hasBatchAssetsList) {
      selectDecisionAllAssets(key as SubmitDecisionDto['decision']);
    }
    selectDecision(key as SubmitDecisionDto['decision']);
  };

  return (
    <div
      className={classNames(
        'dropdown_decisions-mobile',
        'tour-announcement-media-viewer-mobile-decision',
        className,
        {
          'tour-announcement-media-viewer-mobile-decision-active':
            isAnnouncementMediaViewerMobileDecisionTourStep,
          'dropdown_decisions-mobile--open': _isDropdownOpen
        }
      )}
    >
      {_isDropdownOpen &&
        createPortal(
          <div
            className="dropdown_decisions-mobile_overlay-backdrop"
            onClick={() => setIsDropdownOpen(false)}
            role="presentation"
          />,
          document.body
        )}
      <Tooltip
        overlayClassName={classNames('toggle_favorite_overlay', {
          'b-show-arrow': isAnnouncementMediaViewerMobileDecisionTour
        })}
        overlayInnerStyle={{ padding: 0 }}
        title={
          <div className="media-viewer-tooltip-inner" style={{ padding: 8 }}>
            {isAnnouncementMediaViewerMobileDecisionTour && 'Select a decision'}
            {!isAnnouncementMediaViewerMobileDecisionTour && disableReason}
          </div>
        }
        placement="left"
        trigger={isMdWidth ? [] : ['click']}
        open={isAnnouncementMediaViewerMobileDecisionTour ? true : undefined}
      >
        <div className="dropdown_decisions-mobile_dropdown">
          <Dropdown
            disabled={isButtonDisabled || isDemoOrganization}
            menu={{
              multiple: false,
              selectedKeys: decision ? [decision] : [],
              items: [
                ...(hasBatchAssetsList
                  ? [
                      {
                        type: 'group' as const,
                        label: (
                          <Checkbox
                            onChange={(e) =>
                              setIsApplyToAllAssets(e.target.checked)
                            }
                            checked={isApplyToAllAssets}
                            className="media_viewer_actions-dropdown-checkbox"
                          >
                            Apply to all media
                          </Checkbox>
                        )
                      }
                    ]
                  : []),
                ...decisions
              ],
              onClick: onDecisionMenuClick
            }}
            prefixCls="media_viewer_menu"
            overlayClassName={classNames(
              'media_viewer_actions-overlay',
              'dropdown_decisions-mobile-overlay'
            )}
            placement="topRight"
            trigger={['click']}
            open={_isDropdownOpen}
            onOpenChange={setIsDropdownOpen}
            getPopupContainer={(triggerNode) =>
              triggerNode.closest('.dropdown_decisions-mobile') || document.body
            }
          >
            <button
              type="button"
              className={classNames(
                'dropdown_decisions-mobile_btn',
                'tour-review-asset-decision',
                {
                  'dropdown_decisions-mobile_btn--open': _isDropdownOpen,
                  'dropdown_decisions-mobile_btn--disabled': isButtonDisabled
                }
              )}
            >
              {_isDropdownOpen ? (
                <CloseSvg />
              ) : isButtonDisabled ? (
                <LockSvg className="b-white-icon" />
              ) : (
                <ApprovalRoutingSvg />
              )}
            </button>
          </Dropdown>
        </div>
      </Tooltip>
    </div>
  );
}

export default DropdownDecisionsAssetMobile;
